import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import FeaturesSection from "./components/aboutSection";
import AnimatedLogosSection from "./components/animation-section";
import FunctionSection from "./components/appFunctions";
import FAQ from "./components/faq";
import Footer from "./components/Footer";
import HeroSection from "./components/Hero-Section";
import Navbar from "./components/navbar";
import NewsletterSubscription from "./components/newsletter";
import ReviewsCarousel from "./components/reviews";
import DownloadAppSection from "./components/DownloadAppSection";
import LandingPage from "./components/LandingPage";
import FarmManagementRoute from "./components/FarmManagementRoute";
import AuthPage from "./Auth/AuthPage";
import About from "./About";

import Login from "./Auth/login";
import ForgotPassword from "./Auth/ForgotPassword";
import ResetPassword from "./Auth/ResetPassword";

import Loader from "./components/Loader";
import ProtectedRoute from "./components/ProtectedRoute";
import RoleBasedRoute, { DashboardRedirect } from "./components/RoleBasedRoute";
import DashboardHome from "./components/Dashboard/DashboardOverview";
import DashboardLayout from "./components/Dashboard/DashboardLayout";
import ProductView from "./components/Dashboard/ProductView";
import ProductCare from "./components/Dashboard/ProductCare";
import FarmManagement from "./components/Dashboard/DashboardManagement";
import MyProducts from "./components/Dashboard/MyProduce";
import AgricExpertPage from "./components/Dashboard/AgricExperts";
import AgricExpertsList from "./components/Dashboard/AgricExperts";
import ExpertMessaging from "./components/Dashboard/Messages";
import OrdersPage from "./components/retailersDashboard/Orders";

import ExpertDetailsPage from "./components/Dashboard/ExpertDetails";
import AppointmentsPage from "./components/Dashboard/Apointments";
import MessagesPage from "./components/Dashboard/Messages";
// import AIAssistantPage from "./components/Dashboard/AIAssistant";
// import AiAssistant from "./components/Dashboard/AIAssistant";
import SettingsPage from "./components/Dashboard/settings";
import AccountSettings from "./components/Dashboard/accountsettings";
import MyOrders from "./components/Dashboard/MyOrders";
import Withdrawals from "./components/Dashboard/Withdrawals";
import ExpertChat from "./components/Dashboard/chat";
import AIChat from "./components/Dashboard/AIAssistant";
import PrivacySecuritySettings from "./components/Dashboard/Privacysettings";
import FarmerSellPage from "./components/Dashboard/Sell";
import MyProduce from "./components/Dashboard/MyProduce";
import NotificationSettings from "./components/Dashboard/NotificationSettings";
import ManageProduce from "./components/Dashboard/ManageProduce";
import ProduceView from "./components/Dashboard/ProduceView";
import ExpertOverview from "./components/expertDashboard/expertOverview";
import EarningsPage from "./components/expertDashboard/earnings";
import ExpertWithdrawals from "./components/expertDashboard/expertWithdrawals";
import ExpertAppointmentsPage from "./components/expertDashboard/expertApointments";
import AppointmentDetails from "./components/expertDashboard/apointmentDetails";
import NotificationsPage from "./components/Dashboard/Notifications";
import ExpertSettingsPage from "./components/expertDashboard/expertsettings";
import ExpertAccountSettings from "./components/expertDashboard/experAccountSettings";
import RetailerOverview from "./components/retailersDashboard/RetailersOverview";
import OrderDetailsPageRtl from "./components/retailersDashboard/OrderViewRetail";
import MyOrdersPageRtl from "./components/retailersDashboard/retailersOreders";
import ExpertiseManagement from "./components/expertDashboard/expert-experties-settings";
import AvailabilitySettings from "./components/expertDashboard/expert-availability-settings";
import ExpertPrivacySecuritySettings from "./components/expertDashboard/expert-privacy-settings";
import OfflineIndicator from "./components/OfflineIndicator";
import offlineFirstService from "./services/offlineFirstService";
import preloadService from "./services/preloadService";
import { LanguageProvider } from "./context/LanguageContext";
import Marketplace from "./components/Marketplace/Marketplace";
import CartPage from "./components/Marketplace/Cart";
import OrderSuccess from "./components/Marketplace/OrderSuccess";

function App() {
  const [isLoading, setIsLoading] = useState(false); // Disabled loading screen temporarily
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    // Disabled loading screen to fix refresh loop
    /*
    const timer = setTimeout(() => {
      setIsFading(true);
      setTimeout(() => setIsLoading(false), 500); // Fade out duration
    }, 4000);

    return () => clearTimeout(timer);
    */
  }, []);

  // Initialize offline-first functionality only when user is logged in
  useEffect(() => {
    // Check token immediately - don't initialize anything if no token
    const hasToken = localStorage.getItem('authToken');
    if (!hasToken) {
      return; // Exit early - no token means no initialization
    }
    
    const initializeOfflineFirst = async () => {
      try {
        // Double-check token before initializing (in case it was removed)
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
          return;
        }
        
        // Initialize the comprehensive offline-first service
        await offlineFirstService.initialize();
        
        // Legacy preload service as fallback/backup
        if (navigator.onLine) {
          try {
            await preloadService.startPreload();
          } catch (error) {
            // Silent fail
          }
        }
        
        // Schedule periodic refresh for legacy service
        preloadService.schedulePeriodicRefresh(30); // Every 30 minutes
      } catch (error) {
        // Silent fail
      }
    };

    // Initialize only if user is logged in
    initializeOfflineFirst();
    
    return () => {
      preloadService.stopPeriodicRefresh();
    };
  }, []);

  return (
    <LanguageProvider>
    <AuthProvider>
      <Router>
        <PWAHandler />
        <Routes>
        {/* Main site routes */}
        <Route path="/" element={<LandingPage />} />
        <Route
          path="/farm-management"
          element={
            <FarmManagementRoute>
              <div className="main-body">
                <Navbar />
                <HeroSection />
                <FunctionSection />
                <AnimatedLogosSection />
                <ReviewsCarousel />
                <DownloadAppSection />
                <FAQ />
                <NewsletterSubscription />
                <Footer />
              </div>
            </FarmManagementRoute>
          }
        />
        <Route path="/about" element={<About />} />
        <Route path="/signup" element={<AuthPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/forgotpassword/:token" element={<ResetPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        
        {/* Marketplace routes (public) */}
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/marketplace/cart" element={<CartPage />} />
        <Route path="/marketplace/success" element={<OrderSuccess />} />

        {/* Farmer Dashboard routes */}
        <Route path="/farmer-dashboard" element={
          <ProtectedRoute>
            <RoleBasedRoute allowedRoles={['Farmer']}>
              <DashboardLayout />
            </RoleBasedRoute>
          </ProtectedRoute>
        }>
          <Route index element={<DashboardHome />} />
          
          {/* Farm management */}
          <Route path="myfarm" element={<FarmManagement />} />
          <Route path="myfarm/manage/:itemid" element={<ManageProduce />} />

          {/* Products/produce routes */}
          <Route path="product">
            <Route path=":productId" element={<ProductView />} />
            <Route path=":productId/care" element={<ProductCare />} />
          </Route>

          <Route path="produce">
            <Route index element={<MyProduce />} />
            <Route path=":id" element={<ProduceView />} />
          </Route>

          {/* Experts routes */}
          <Route path="experts">
            <Route index element={<AgricExpertsList />} />
            <Route path=":id" element={<ExpertDetailsPage />} />
          </Route>

          {/* Sales routes */}
          <Route path="sell" element={<FarmerSellPage />} />
          <Route path="notifications" element={<NotificationsPage />} />

          {/* AI routes - Commented out for backend implementation later */}
          { <Route path="ai" element={<AIChat />} />}

          {/* Orders routes */}
          <Route path="orders" element={<MyOrders />} />

          {/* Withdrawals routes */}
          <Route path="withdrawals" element={<Withdrawals />} />

          {/* Settings routes */}
          <Route path="settings">
            <Route index element={<SettingsPage />} />
            <Route path="account" element={<AccountSettings />} />
            <Route path="privacy&security" element={<PrivacySecuritySettings />} />
            <Route path="notifications" element={<NotificationSettings />} />
          </Route>

          {/* Messages routes */}
          <Route path="messages">
            <Route index element={<MessagesPage />} />
            <Route path="chat/:expertId" element={<ExpertChat />} />
          </Route>

          {/* Appointments */}
          <Route path="appointments" element={<AppointmentsPage />} />
        </Route>

        {/* Expert Dashboard routes */}
        <Route path="/expert-dashboard" element={
          <ProtectedRoute>
            <RoleBasedRoute allowedRoles={['Expert']}>
              <DashboardLayout />
            </RoleBasedRoute>
          </ProtectedRoute>
        }>
          <Route index element={<ExpertOverview />} />
          <Route path="earnings" element={<EarningsPage />} />
          <Route path="transactions" element={<EarningsPage />} />
          <Route path="withdrawals" element={<ExpertWithdrawals />} />
          <Route path="messages">
            <Route index element={<MessagesPage />} />
            <Route path="chat/:expertId" element={<ExpertChat />} />
          </Route>
          {/* <Route path="ai" element={<AIAssistantPage />} /> */}
          <Route path="appointments/:id" element={<AppointmentDetails />} />
          <Route path="appointments" element={<ExpertAppointmentsPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          
          {/* Settings routes */}
          <Route path="settings">
            <Route index element={<ExpertSettingsPage />} />
            <Route path="account" element={<ExpertAccountSettings />} />
            <Route path="privacy&security" element={<ExpertPrivacySecuritySettings />} />
            <Route path="availability" element={<AvailabilitySettings />} />
            <Route path="expertise" element={<ExpertiseManagement />} />
          </Route>
        </Route>

        {/* Retailer Dashboard routes */}
        <Route path="/retailer-dashboard" element={
          <ProtectedRoute>
            <RoleBasedRoute allowedRoles={['Retailer']}>
              <DashboardLayout />
            </RoleBasedRoute>
          </ProtectedRoute>
        }>
          <Route index element={<RetailerOverview />} />
          <Route path="products" element={<Navigate to="/marketplace" replace />} />
          <Route path="inventory" element={<Navigate to="/marketplace" replace />} />
          <Route path="orders" element={<MyOrdersPageRtl />} />
          <Route path="orders/:Id" element={<OrderDetailsPageRtl />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="messages" element={<MessagesPage />} />
          
          {/* Settings routes */}
          <Route path="settings">
            <Route index element={<SettingsPage />} />
            <Route path="account" element={<AccountSettings />} />
            <Route path="privacy&security" element={<PrivacySecuritySettings />} />
            <Route path="notifications" element={<NotificationSettings />} />
          </Route>
        </Route>

        {/* Legacy dashboard redirect */}
        <Route path="/dashboard" element={<DashboardRedirect />} />
        </Routes>
        
        {/* Offline Indicator - Temporarily disabled to fix loading issues */}
       {/*  <OfflineIndicator /> */}
      </Router>
    </AuthProvider>
    </LanguageProvider>
  );
}

// PWA Detection Component - redirects to dashboard when PWA is opened
function PWAHandler() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  useEffect(() => {
    // Check if app is installed as PWA
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  window.navigator.standalone ||
                  document.referrer.includes('android-app://');
    
    if (isPWA && window.location.pathname === '/') {
      // If PWA is opened, redirect to dashboard
      if (isAuthenticated && user) {
        const role = user.role?.toLowerCase();
        if (role === 'expert') {
          navigate('/expert-dashboard', { replace: true });
        } else if (role === 'retailer') {
          navigate('/retailer-dashboard', { replace: true });
        } else {
          navigate('/farmer-dashboard', { replace: true });
        }
      } else {
        // Not logged in, redirect to login
        navigate('/login', { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate]);
  
  return null;
}

export default App;
